import {
  NOTICE_BACKDROPS,
  NOTICE_COLOR_KEYS,
  NOTICE_CONTROL_STYLES,
  NOTICE_DESKTOP_POSITIONS,
  NOTICE_HEX_COLOR_RE,
  NOTICE_LOGO_SIZE_MAX,
  NOTICE_LOGO_SIZE_MIN,
  NOTICE_MOBILE_LAYOUTS,
  NOTICE_PHONE_FOOTERS,
  NOTICE_RADIUS_MAX,
  NOTICE_RADIUS_MIN,
  NOTICE_SELECTION_CONTROLS,
  NOTICE_TEXT_SCALES,
  NOTICE_WIDTH_MAX,
  NOTICE_WIDTH_MIN,
} from "./constants";
import { isSafeCustomCss } from "./customCss";
import { resolveAppearance } from "./registry";
import type {
  NoticeAppearance,
  NoticeAppearanceColors,
  ResolvedNoticeAppearance,
} from "./types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const pickColors = (raw: unknown): NoticeAppearanceColors => {
  if (!isRecord(raw)) {
    return {};
  }
  const colors: NoticeAppearanceColors = {};
  for (const key of NOTICE_COLOR_KEYS) {
    const value = raw[key];
    if (typeof value === "string" && NOTICE_HEX_COLOR_RE.test(value)) {
      colors[key] = value;
    }
  }
  return colors;
};

/** A number clamped into range as a px length; anything else is unset. */
const pickPx = (raw: unknown, min: number, max: number): string | undefined => {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return undefined;
  }
  return `${Math.min(max, Math.max(min, Math.round(raw)))}px`;
};

/** One of `options`, or unset. */
const pickOption = <T extends string>(
  options: ReadonlyArray<T>,
  raw: unknown,
): T | undefined => options.find((option) => option === raw);

const pickBoolean = (raw: unknown): boolean | undefined =>
  typeof raw === "boolean" ? raw : undefined;

const pickCustomCss = (raw: unknown): string | undefined =>
  typeof raw === "string" && raw.trim() && isSafeCustomCss(raw)
    ? raw
    : undefined;

/**
 * Validates `active_config.appearance` into what components paint with. The
 * server already normalises it, but an older ledger row, a proxy or a hand edit
 * can still serve junk, and a notice must render whatever arrives: anything
 * unrecognised is dropped, and a missing value leaves the classic default.
 */
export const resolveNoticeAppearance = (
  raw: NoticeAppearance | null | undefined,
): ResolvedNoticeAppearance => {
  const appearance = isRecord(raw) ? raw : {};
  return {
    style: resolveAppearance(appearance.style),
    colors: pickColors(appearance.colors),
    radius: pickPx(
      appearance.border_radius,
      NOTICE_RADIUS_MIN,
      NOTICE_RADIUS_MAX,
    ),
    selectionControl: pickOption(
      NOTICE_SELECTION_CONTROLS,
      appearance.selection_control,
    ),
    controlStyle: pickOption(NOTICE_CONTROL_STYLES, appearance.control_style),
    confirmBeforeSubmit: appearance.confirm_before_submit === true,
    mobileLayout: pickOption(NOTICE_MOBILE_LAYOUTS, appearance.mobile_layout),
    desktopPosition: pickOption(
      NOTICE_DESKTOP_POSITIONS,
      appearance.desktop_position,
    ),
    backdrop: pickOption(NOTICE_BACKDROPS, appearance.backdrop),
    collapsibleSections: pickBoolean(appearance.collapsible_sections),
    phoneFooter: pickOption(NOTICE_PHONE_FOOTERS, appearance.phone_footer),
    autoDark: appearance.auto_dark === true,
    darkColors: pickColors(appearance.dark_colors),
    maxWidth: pickPx(appearance.max_width, NOTICE_WIDTH_MIN, NOTICE_WIDTH_MAX),
    logoSize: pickPx(
      appearance.logo_size,
      NOTICE_LOGO_SIZE_MIN,
      NOTICE_LOGO_SIZE_MAX,
    ),
    textScale: pickOption(NOTICE_TEXT_SCALES, appearance.text_scale),
    motion: pickBoolean(appearance.motion),
    customCss: pickCustomCss(appearance.custom_css),
  };
};

/**
 * The palette to paint with: the dark one while the device prefers a dark
 * scheme and the admin turned automatic dark mode on with a dark palette set.
 * The dark palette replaces the light one whole, so a light colour never
 * leaks onto a dark surface.
 */
export const withColorScheme = (
  appearance: ResolvedNoticeAppearance,
  prefersDark: boolean,
): ResolvedNoticeAppearance =>
  appearance.autoDark &&
  prefersDark &&
  Object.keys(appearance.darkColors).length > 0
    ? { ...appearance, colors: appearance.darkColors }
    : appearance;
